import React, { useState, useEffect, useRef, useCallback } from "react";
import { FormattedHTMLMessage } from "react-intl";
import { debounce } from "debounce";

import { logger } from "@jslib/shared/utils/logger";
import { useSearchRanking } from "@jslib/OrgSearch";

import { fetchOrganization } from "../lib/ServerApi/OrganizationApiClient";
import { type OrganizationSearchResp, type DatabaseId, type Locale } from "../lib/types/types";
import { DEBOUNCE_TIMEOUT, DEFAULT_LOCALE } from "../constants";
import { EventName, type OrgsDisplayedEvent, recordGaEvent } from "../lib/GoogleAnalytics/ga";
import { getQueryParam } from "../lib/utils/routing/Url";

import { LinkExternal } from "./LinkExternal";
import { LoadingSpinnerComponent } from "./LoadingSpinner/LoadingSpinnerComponent";
import { OrganizationListComponent } from "./OrganizationList/OrganizationListComponent";
import { MetricIds, useMetrics } from "./FeatureFlags/useMetrics";

interface FetchOrganizationsProps {
  getItemProps: any;
  highlightedIndex: any;
  itemToString: any;
  minimumSearchValueLength: number;
  onLoaded: any;
  programId: DatabaseId;
  orgSearchUrl?: string;
  locale?: Locale;
  urlAddSchoolForm?: string;
  openOrgSearchEnabled?: boolean;
  onClickAddSchool?: () => void;
  onTipsShown?: () => void;
  // optional search params that are set before the query is sent
  searchUrlParams?: Record<string, string>;
  /** @deprecated */
  // eslint-disable-next-line react/no-unused-prop-types
  countryCode?: string;
  /** @deprecated orgSearchTags has no affect, pass a full orgSearchUrl, instead */
  // eslint-disable-next-line react/no-unused-prop-types
  orgSearchTags?: string;
  /** @deprecated pass a complete orgSearchUrl instead, using the `name` parameter with a value of what you would have given `searchValue` */
  searchValue?: string;
  /** @deprecated. Menu is always mounted now, up in TypeaheadComponent. */
  getMenuProps?: any;
}

type AddSchoolRequestRowProps = {
  programId: DatabaseId;
  locale: Locale;
  urlAddSchoolForm: string;
  onClick?: () => void;
  onTipsShown?: () => void;
};

const LightbulbIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"
    />
  </svg>
);

const AddSchoolRequestRow: React.FC<AddSchoolRequestRowProps> = ({
  urlAddSchoolForm,
  programId,
  locale = DEFAULT_LOCALE,
  onClick = () => undefined,
  onTipsShown,
}: AddSchoolRequestRowProps) => {
  const [shouldShowTips, setShouldShowTips] = React.useState(false);
  const { trackEvent } = useMetrics({ programId });

  const handleOnClick = () => {
    recordGaEvent({
      eventName: EventName.ASR_TIPS_SHOWN,
      params: { programId, locale },
    });
    trackEvent(MetricIds.AsrTipsShown);
    // Notify parent that tips are now shown
    onTipsShown?.();

    setShouldShowTips(true);
  };

  if (shouldShowTips) {
    return (
      <div className="sid-organization-list__add-school-tips">
        <div className="sid-organization-list__tips">
          <div>
            <LightbulbIcon className="sid-organization-list__tips-icon" />
          </div>

          <div className="sid-organization-list__tips-text">
            <FormattedHTMLMessage
              id="multipleLocationsTips"
              defaultMessage="If your academic institution has multiple locations and you didn't see yours, enter the main location."
            />
            <FormattedHTMLMessage
              id="tryFullNameTips"
              defaultMessage="Try typing the full name of the academic institution rather than using acronyms or abbreviations."
            />
          </div>
        </div>

        <LinkExternal
          onClick={onClick}
          className="sid-organization-list__item sid-organization-list__request-school sid-link"
          href={`${urlAddSchoolForm}?pid=${programId}&locale=${locale}`}
        >
          <FormattedHTMLMessage
            id="requestSchool"
            defaultMessage="Don't see your school? Request to have it added"
          />
        </LinkExternal>
      </div>
    );
  }

  return (
    <LinkExternal
      onClick={handleOnClick}
      className="sid-organization-list__item sid-organization-list__request-school sid-link"
    >
      <FormattedHTMLMessage
        id="cantFindYourSchool"
        defaultMessage="Can't find your school? Click to show tips"
      />
    </LinkExternal>
  );
};

const getSearchValue = (orgSearchUrl: string): string => getQueryParam("name", orgSearchUrl) || "";

export const getAttribution = (organizations: OrganizationSearchResp): React.ReactNode => {
  if (!organizations) {
    return null;
  }

  // When organization results are provided by Google Place, Google requires their logo be presented as attribution.
  // org.source denotes where the organization data is coming from, in case of Google Place, it is "PLACE".
  const requiresGooglePlaceAttribution = organizations.find((org) => org.source === "PLACE");
  if (requiresGooglePlaceAttribution) {
    return (
      <img
        className="sid-organization-list__attribution-logo"
        src="https://developers.google.com/static/maps/documentation/images/google_on_white.png"
        alt="Google"
      />
    );
  }
  return null;
};

/**
 * Support deprecated prop "searchValue"
 * @TODO remove searchValue prop and this function in v2
 * @param orgSearchUrl
 */
export const updateSearchUrl = (
  orgSearchUrl: string,
  searchValue: string,
  newParams?: Record<string, string>,
): string => {
  if (!searchValue && !newParams) {
    return orgSearchUrl;
  }
  if (!orgSearchUrl) {
    logger.error("Expected orgSearchUrl");
    return orgSearchUrl;
  }

  try {
    const url = new URL(orgSearchUrl);
    const { origin, pathname, search } = url;
    const params = new URLSearchParams(search);
    if (searchValue) {
      params.set("name", searchValue);
    }
    if (newParams) {
      Object.entries(newParams).forEach(([k, v]) => {
        params.set(k, v);
      });
    }
    params.sort();
    return `${origin}${pathname}?${params.toString()}`;
  } catch (e) {
    logger.error(e);
  }
};

const FetchOrganizations: React.FC<FetchOrganizationsProps> = ({
  getItemProps,
  getMenuProps,
  highlightedIndex,
  itemToString,
  minimumSearchValueLength,
  onLoaded,
  programId,
  orgSearchUrl,
  locale,
  urlAddSchoolForm,
  openOrgSearchEnabled,
  onClickAddSchool,
  onTipsShown,
  searchUrlParams,
  searchValue,
}) => {
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationSearchResp>(null);
  const [isErrored, setIsErrored] = useState<string>(null);
  const mountedRef = useRef(false);
  const prevSearchTermRef = useRef<string>(null);

  const searchRanking = useSearchRanking();

  // Store props in refs so debounced fetch always uses latest values (like this.props in class)
  const propsRef = useRef({ orgSearchUrl, searchValue, searchUrlParams, onLoaded });
  propsRef.current = { orgSearchUrl, searchValue, searchUrlParams, onLoaded };

  // Store search ranking in ref for debounced fetch
  const searchRankingRef = useRef(searchRanking);
  searchRankingRef.current = searchRanking;

  const searchTerm = searchValue || getSearchValue(orgSearchUrl) || "";

  // Stable fetch function that reads from refs (like this.fetch in class component)
  const doFetch = useCallback(async () => {
    if (!mountedRef.current) {
      return;
    }

    const {
      orgSearchUrl: url,
      searchValue: sv,
      searchUrlParams: params,
      onLoaded: loaded,
    } = propsRef.current;

    // Build params including location if distance sorting is enabled
    let fetchParams = params;
    const ranking = searchRankingRef.current;
    if (ranking && ranking.sortMode === "distance" && ranking.userLocation) {
      fetchParams = {
        ...params,
        lat: String(ranking.userLocation.latitude),
        long: String(ranking.userLocation.longitude),
      };
    }

    const start = Date.now();
    const fetchedOrganizations: OrganizationSearchResp = await fetchOrganization(
      updateSearchUrl(url, sv, fetchParams),
    );

    if (!mountedRef.current) {
      return;
    }

    if (fetchedOrganizations) {
      loaded(fetchedOrganizations);
      setOrganizations(fetchedOrganizations);
      setLoading(false);
    }

    try {
      const currentSearchTerm = sv || getSearchValue(url) || "";
      const event: OrgsDisplayedEvent = {
        eventName: EventName.ORGS_DISPLAYED,
        params: {
          queryLength: currentSearchTerm.length,
          eventDuration: Date.now() - start,
          numberOfResults: fetchedOrganizations?.length,
        },
      };
      recordGaEvent(event);
    } catch (e) {
      logger.error(e);
    }
  }, []); // No dependencies - reads from refs

  // Create a stable debounced function once
  const debouncedFetchRef = useRef<ReturnType<typeof debounce>>(null);
  if (!debouncedFetchRef.current) {
    debouncedFetchRef.current = debounce(doFetch, DEBOUNCE_TIMEOUT);
  }

  // Handle mount/unmount
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (debouncedFetchRef.current) {
        debouncedFetchRef.current.clear();
      }
    };
  }, []);

  // Handle search term changes (replaces componentDidMount and componentDidUpdate logic)
  useEffect(() => {
    const prevSearchTerm = prevSearchTermRef.current;

    // On mount or when search term changes
    if (prevSearchTerm !== searchTerm) {
      if (searchTerm.length >= minimumSearchValueLength) {
        // Reset state and trigger fetch
        setIsErrored(null);
        setLoading(true);
        setOrganizations(null);
        if (debouncedFetchRef.current) {
          debouncedFetchRef.current();
        }
      } else {
        // Reset state without fetching
        setIsErrored(null);
        setLoading(false);
        setOrganizations(null);
        if (prevSearchTerm !== null) {
          // Only log on updates, not initial mount with short search term
          logger.info(
            `No organizations fetched. Search searchTterm "${searchTerm}" is less than ${minimumSearchValueLength} characters.`,
          );
        } else if (searchTerm.length > 0) {
          // Log on initial mount if there's a search term that's too short
          logger.info(
            `No organizations fetched. Search searchTerm "${searchTerm}" is less than ${minimumSearchValueLength} characters.`,
          );
        }
      }
    }

    prevSearchTermRef.current = searchTerm;
  }, [searchTerm, minimumSearchValueLength]);

  // Re-fetch when sort mode or user location changes (but not on initial render)
  const sortMode = searchRanking?.sortMode;
  const userLatitude = searchRanking?.userLocation?.latitude;
  const userLongitude = searchRanking?.userLocation?.longitude;

  useEffect(() => {
    if (searchTerm.length >= minimumSearchValueLength) {
      setIsErrored(null);
      setLoading(true);
      setOrganizations(null);
      debouncedFetchRef.current?.();
    }
  }, [sortMode, userLatitude, userLongitude, searchTerm, minimumSearchValueLength]);

  const orgSearchAttribution = getAttribution(organizations);

  if (loading) {
    return (
      <div className="sid-organization-list__menu">
        <div className="sid-organization-list__message sid-organization-list__spinner-container">
          <div id="sid-spinner-container">
            <LoadingSpinnerComponent />
          </div>
        </div>
      </div>
    );
  }
  if (isErrored) {
    return (
      <div className="sid-organization-list__menu">
        <div className="sid-organization-list__message">
          {/* TODO: Define correct message ID from backend (backend need to send this message?) */}
          <FormattedHTMLMessage
            id="errorId.unknownError"
            defaultMessage="An unknown error occurred"
          />
        </div>
      </div>
    );
  }

  if (organizations && organizations.length > 0) {
    return (
      <>
        {orgSearchAttribution && (
          <div className="sid-organization-list__attribution">{orgSearchAttribution}</div>
        )}

        <OrganizationListComponent
          getItemProps={getItemProps}
          getMenuProps={getMenuProps}
          highlightedIndex={highlightedIndex}
          itemToString={itemToString}
          organizations={organizations}
        >
          {searchTerm &&
            !openOrgSearchEnabled &&
            urlAddSchoolForm &&
            (!organizations || organizations.length < 26) && (
              <AddSchoolRequestRow
                onClick={onClickAddSchool}
                onTipsShown={onTipsShown}
                urlAddSchoolForm={urlAddSchoolForm}
                programId={programId}
                locale={locale}
              />
            )}
        </OrganizationListComponent>
      </>
    );
  }

  if (
    searchTerm &&
    !openOrgSearchEnabled &&
    urlAddSchoolForm &&
    (!organizations || organizations.length === 0)
  ) {
    return (
      <div className="sid-organization-list__menu">
        <AddSchoolRequestRow
          onClick={onClickAddSchool}
          onTipsShown={onTipsShown}
          urlAddSchoolForm={urlAddSchoolForm}
          programId={programId}
          locale={locale}
        />
      </div>
    );
  }

  return null;
};

export const FetchOrganizationsComponent = FetchOrganizations;
